import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useLang } from '../contexts/LangContext'
import type { Lang } from '../contexts/LangContext'

// Landing section "Agendar demonstração". Posts to the public request-demo
// edge function, which mails contact@ with Reply-To set to the visitor.
// Styles live in LANDING_CSS (src/pages/Landing.tsx), under .lp-demo*.

export type ClientsBand = 'upTo30' | 'to150' | 'to500' | 'over500'
type Period = 'morning' | 'afternoon' | 'evening'
type Status = 'idle' | 'sending' | 'sent' | 'invalid' | 'error' | 'limited'

const CONTACT_EMAIL = 'contact@immigrantshield.com'

const BANDS: ClientsBand[] = ['upTo30', 'to150', 'to500', 'over500']
const PERIODS: Period[] = ['morning', 'afternoon', 'evening']

// Each language named in itself, so a visitor finds theirs whatever the page language.
const MEETING_LANGS: { key: Lang; label: string }[] = [
  { key: 'pt', label: 'Português' },
  { key: 'es', label: 'Español' },
  { key: 'en', label: 'English' },
]

interface Props {
  /** Set by the Enterprise card; `n` changes on every click so the same band re-applies. */
  preset: { band: ClientsBand; n: number } | null
}

export function DemoRequest({ preset }: Props) {
  const { t, lang } = useLang()
  const nameRef = useRef<HTMLInputElement>(null)
  const honeypotRef = useRef<HTMLInputElement>(null)
  const sentRef = useRef<HTMLHeadingElement>(null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [office, setOffice] = useState('')
  const [phone, setPhone] = useState('')
  const [clients, setClients] = useState<ClientsBand | ''>('')
  const [meetingLang, setMeetingLang] = useState<Lang>(lang)
  const [meetingLangTouched, setMeetingLangTouched] = useState(false)
  const [period, setPeriod] = useState<Period | ''>('')
  const [status, setStatus] = useState<Status>('idle')

  // The meeting language follows the page language until the visitor picks one.
  useEffect(() => {
    if (!meetingLangTouched) setMeetingLang(lang)
  }, [lang, meetingLangTouched])

  // The form unmounts on success; move focus to the confirmation so keyboard and
  // screen-reader users are not dropped on <body>.
  useEffect(() => {
    if (status === 'sent') sentRef.current?.focus()
  }, [status])

  useEffect(() => {
    if (!preset) return
    setClients(preset.band)
    nameRef.current?.focus({ preventScroll: true })
  }, [preset])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (status === 'sending') return
    setStatus('sending')
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, email, office, phone, clients, meetingLang, period,
          pageLang: lang,
          website: honeypotRef.current?.value ?? '',
        }),
      })
      if (res.ok) setStatus('sent')
      else if (res.status === 400) setStatus('invalid')
      else setStatus(res.status === 429 ? 'limited' : 'error')
    } catch {
      setStatus('error')
    }
  }

  const contactLink = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>

  return (
    <div className="lp-demo">
      <div>
        <p className="lp-eyebrow">{t('landing.demo.eyebrow')}</p>
        <h2>{t('landing.demo.title')}</h2>
        <p className="lp-demo-lead">{t('landing.demo.body')}</p>
        <ul className="lp-demo-points">
          <li>{t('landing.demo.p1')}</li>
          <li>{t('landing.demo.p2')}</li>
          <li>{t('landing.demo.p3')}</li>
        </ul>
      </div>

      {status === 'sent' ? (
        <div className="lp-form" role="status">
          <div className="lp-sent">
            <div className="lp-sent-ico" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
            </div>
            <h3 ref={sentRef} tabIndex={-1}>{t('landing.demo.sentTitle')}</h3>
            <p>{t('landing.demo.sentBody')} <strong>{email}</strong>.</p>
          </div>
        </div>
      ) : (
        <form className="lp-form" onSubmit={onSubmit}>
          <div className="lp-form-grid">
            <div className="lp-field">
              <label htmlFor="demo-name">{t('landing.demo.name')}</label>
              <input id="demo-name" ref={nameRef} className="lp-input" required maxLength={120} autoComplete="name"
                value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="lp-field">
              <label htmlFor="demo-email">{t('landing.demo.email')}</label>
              <input id="demo-email" type="email" className="lp-input" required maxLength={254} autoComplete="email"
                value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="lp-field">
              <label htmlFor="demo-office">{t('landing.demo.office')}</label>
              <input id="demo-office" className="lp-input" required maxLength={160} autoComplete="organization"
                placeholder={t('landing.demo.officePh')} value={office} onChange={e => setOffice(e.target.value)} />
            </div>
            <div className="lp-field">
              <label htmlFor="demo-phone">
                {t('landing.demo.phone')} <span className="lp-hint">· {t('landing.demo.optional')}</span>
              </label>
              <input id="demo-phone" type="tel" className="lp-input" maxLength={25} autoComplete="tel"
                pattern="[0-9+\(\)\.\-\s]{7,25}" placeholder={t('landing.demo.phonePh')} title={t('landing.demo.phoneFormat')}
                value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
            <div className="lp-field lp-span">
              <label htmlFor="demo-clients">{t('landing.demo.clients')}</label>
              <select id="demo-clients" className="lp-input" required value={clients}
                onChange={e => setClients(e.target.value as ClientsBand)}>
                <option value="" disabled>{t('landing.demo.clientsPh')}</option>
                {BANDS.map(b => <option key={b} value={b}>{t(`landing.demo.${b}`)}</option>)}
              </select>
            </div>

            <fieldset className="lp-field lp-span lp-fieldset">
              <legend className="lp-label">{t('landing.demo.meetingLang')}</legend>
              <div className="lp-seg">
                {MEETING_LANGS.map(o => (
                  <label key={o.key} className="lp-seg-opt">
                    <input type="radio" name="demo-meeting-lang" value={o.key} checked={meetingLang === o.key}
                      onChange={() => { setMeetingLang(o.key); setMeetingLangTouched(true) }} />
                    <span>{o.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="lp-field lp-span lp-fieldset">
              <legend className="lp-label">
                {t('landing.demo.period')} <span className="lp-hint">· {t('landing.demo.periodHint')}</span>
              </legend>
              <div className="lp-seg">
                {PERIODS.map(p => (
                  <label key={p} className="lp-seg-opt">
                    <input type="radio" name="demo-period" value={p} required checked={period === p}
                      onChange={() => setPeriod(p)} />
                    <span>{t(`landing.demo.${p}`)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          {/* Honeypot: off-screen and out of the tab order; a person never fills it. */}
          <div className="lp-hp" aria-hidden="true">
            <label htmlFor="demo-website">Website</label>
            <input id="demo-website" ref={honeypotRef} name="website" tabIndex={-1} autoComplete="off" />
          </div>

          <button type="submit" className="lp-btn lp-btn-primary" disabled={status === 'sending'}>
            {status === 'sending' ? t('landing.demo.sending') : t('landing.demo.submit')}
          </button>

          {status === 'invalid' && <p className="lp-form-msg" role="alert">{t('landing.demo.invalid')} {contactLink}.</p>}
          {status === 'error' && <p className="lp-form-msg" role="alert">{t('landing.demo.error')} {contactLink}.</p>}
          {status === 'limited' && <p className="lp-form-msg" role="alert">{t('landing.demo.limited')} {contactLink}.</p>}
          {(status === 'idle' || status === 'sending') && <p className="lp-fine">{t('landing.demo.fine')}</p>}
        </form>
      )}
    </div>
  )
}
